import { ContentState } from "draft-js";
import { List } from "immutable";
import { Observable, WritableObservable } from "micro-observables";
import { PaginatedData } from "@core/paginatedData";
import { MappedStore } from "@core/store/mappedStore";
import { SimpleMappedStorePresentation } from "@core/store/mappedStorePresentation";
import { SimpleStore } from "@core/store/store";
import { StorePresentation } from "@core/store/storePresentation";
import { CurrentProfileService } from "@domain/currentProfile/currentProfileService";
import { Group } from "@domain/group/group";
import { GroupService } from "@domain/group/groupService";
import { Quiz } from "@domain/lms/quiz/quiz";
import { QuizService } from "@domain/lms/quiz/quizService";
import { Organization } from "@domain/organization/organization";
import { OrganizationService } from "@domain/organization/organizationService";
import { Profile } from "@domain/user/profile";
import { UserService } from "@domain/user/userService";
import { PostComment } from "@domain/post/comment/postComment";
import { Post, PostReactionType } from "./post";
import { PostApi } from "./postApi";
import { PostDraft } from "./postDraft";
export type FeedSource = "organization" | "user" | "group";
export class PostService {
	postStore = new SimpleStore((id) => this.postApi.get(id), {
		populate: (posts) =>
			Observable.from(
				posts,
				this.postCommentStore.itemsById,
				this.organizationService.organizationStore.itemsById,
				this.groupService.groupStore.itemsById,
				this.quizService.quizStore.itemsById
			).transform(([posts, comments, orgs, groups, quizzes]) => {
				const retrievePopulatedPost = (dummyPost: Post): Post => {
					const post: Post = {
						...dummyPost,
						comments: {
							...dummyPost.comments,
							last: dummyPost.comments.last ? comments.get(dummyPost.comments.last.id) : undefined,
						},
					};
					if (post?.type === "SHARE_ORGANIZATION") {
						return { ...post, reference: post.reference ? orgs.get(post.reference.id) : undefined };
					}
					if (post?.type === "SHARE_POST") {
						return {
							...post,
							reference: post.reference
								? retrievePopulatedPost(posts.get(post.reference.id) ?? post.reference)
								: undefined,
						};
					}
					if (post?.type === "SHARE_GROUP") {
						return { ...post, reference: post.reference ? groups.get(post.reference.id) : undefined };
					}
					if (post?.type === "SHARE_QUIZ") {
						return { ...post, reference: post.reference ? quizzes.get(post.reference.id) : undefined };
					}
					if (post?.type === "SHARE_CAROUSEL") {
						return { ...post, reference: post.reference ? post.reference : undefined };
					}
					if (post?.type === "SHARE_QUIZ_RESULTS") {
						const realQuiz = post.reference && quizzes.get(post.reference.id);
						return {
							...post,
							reference: post.reference && realQuiz ? { ...realQuiz, userScore: post.reference.userScore } : undefined,
						};
					}
					return post;
				};
				return posts.map(retrievePopulatedPost);
			}),
	});
	private postCommentStore = new SimpleStore((id) => this.postApi.getPostComment(id), {
		populate: (comments) =>
			comments.transform((commentMap) => {
				const retrievePopulatedComment = (dummyComment: PostComment): PostComment => {
					if (!dummyComment.children.last) {
						return dummyComment;
					}
					return {
						...dummyComment,
						children: { ...dummyComment.children, last: commentMap.get(dummyComment.children.last.id) },
					};
				};
				return commentMap.map(retrievePopulatedComment);
			}),
	});
	postCommentPresentation = new SimpleMappedStorePresentation(this.postCommentStore, (postId, page) =>
		this.postApi.listComments(postId, page, this.currentProfileService.organizationAlias.get()?.id)
	);
	commentRepliesPresentation = new SimpleMappedStorePresentation(this.postCommentStore, (commentId, page) =>
		this.postApi.listReplies(commentId, page, this.currentProfileService.organizationAlias.get()?.id)
	);

	homeFeed = new StorePresentation(this.postStore, (page) => this.postApi.list(page));
	usersFeed = new SimpleMappedStorePresentation(this.postStore, (id, page) => this.postApi.getProfilePosts(id, page));
	groupsFeed = new SimpleMappedStorePresentation(this.postStore, (id, page) => this.postApi.list(page, id));
	organizationsFeed = new SimpleMappedStorePresentation(this.postStore, (id, page) =>
		this.postApi.getOrganizationPosts(id, page, this.currentProfileService.organizationAlias.get()?.id)
	);
	likersStore = new SimpleMappedStorePresentation(this.userService.profilesStore, (postId, page) =>
		this.postApi.getReactionProfiles(postId, "likes", page)
	);
	sharersStore = new SimpleMappedStorePresentation(this.userService.profilesStore, (postId, page) =>
		this.postApi.getReactionProfiles(postId, "share", page)
	);
	commentersStore = new SimpleMappedStorePresentation(this.userService.profilesStore, (postId, page) =>
		this.postApi.getReactionProfiles(postId, "comments", page)
	);

	commentersLikesStore = new SimpleMappedStorePresentation(this.userService.profilesStore, (commentId, page) =>
		this.postApi.getReactionCommentsProfiles(commentId, "likes", page)
	);

	hasNewContent = new WritableObservable(false);

	constructor(
		private readonly postApi: PostApi,
		private readonly userService: UserService,
		private readonly organizationService: OrganizationService,
		private readonly groupService: GroupService,
		private readonly quizService: QuizService,
		private readonly currentProfileService: CurrentProfileService
	) {
		this.postStore.onNewElements.add(({ updateId, content: items }) => {
			this.organizationService.organizationStore.merge(
				items
					.filter((post) => post.type === "SHARE_ORGANIZATION")
					.map((post) => post.reference)
					.filter(Boolean) as List<Organization>
			);
			this.groupService.groupStore.merge(
				items
					.filter((post) => post.type === "SHARE_GROUP")
					.map((post) => post.reference)
					.filter(Boolean) as List<Group>
			);
			this.quizService.quizStore.merge(
				items
					.filter((post) => post.type === "SHARE_QUIZ" || post.type == "SHARE_QUIZ_RESULTS")
					.map((post) => post.reference)
					.filter(Boolean) as List<Quiz>
			);
			this.postCommentStore.merge(items.map((post) => post.comments.last).filter(Boolean) as List<PostComment>);
			if (updateId !== "post-reference") {
				this.postStore.merge(
					items
						.filter((post) => post.type === "SHARE_POST")
						.map((post) => post.reference)
						.filter(Boolean) as List<Post>,
					"post-reference"
				);
			}
		});

		this.postCommentStore.onNewElements.add(({ updateId, content: items }) => {
			if (updateId !== "comment-reference") {
				this.postCommentStore.merge(
					items.map((comment) => comment.children.last).filter(Boolean) as List<PostComment>,
					"comment-reference"
				);
			}
		});
	}

	async createPost(draft: PostDraft): Promise<Post> {
		const post = await this.postApi.create(draft, this.currentProfileService.organizationAlias.get()?.id);
		this.postStore.save(post);
		return post;
	}

	async updatePost(id: string, draft: PostDraft): Promise<Post> {
		const post = await this.postApi.update(id, draft, this.currentProfileService.organizationAlias.get()?.id);
		this.postStore.save(post);
		return post;
	}

	async sharePost(id: string, message?: ContentState, groupId?: string): Promise<void> {
		const sharedPost = await this.postApi.share(
			id,
			message,
			this.currentProfileService.organizationAlias.get()?.id,
			groupId
		);
		this.postStore.save(sharedPost);
		this.postStore.update(id, (initial) => ({ ...initial, shares: { byMe: true, total: initial.shares.total + 1 } }));
	}

	async togglePostLike(post: Post): Promise<void> {
		const initialLiked = post.likes.byMe;

		const likedPost = (post: Post): Post => ({
			...post,
			likes: { byMe: !initialLiked, total: post.likes.total + (initialLiked ? -1 : 1) },
		});

		this.postStore.update(post.id, (initial) => likedPost(initial));
		await (initialLiked
			? this.postApi.unLike(post.id, this.currentProfileService.organizationAlias.get()?.id)
			: this.postApi.like(post.id, this.currentProfileService.organizationAlias.get()?.id));
	}

	async delete(id: string): Promise<void> {
		await this.postApi.delete(id, this.currentProfileService.organizationAlias.get()?.id);
		this.postStore.remove(id);
	}

	async comment(postId: string, text: ContentState, commentId?: string, parentId?: string): Promise<PostComment> {
		const result = await (commentId
			? this.postApi.updateComment(
					commentId,
					{ text, parentId },
					this.currentProfileService.organizationAlias.get()?.id
				)
			: this.postApi.comment(postId, { text, parentId }, this.currentProfileService.organizationAlias.get()?.id));

		this.postCommentStore.save(result);
		if (parentId) {
			this.commentRepliesPresentation.updateContent(parentId, (replies) => replies.unshift(result));
			this.postCommentStore.update(parentId, (comment) => ({
				...comment,
				children: { ...comment.children, total: comment.children.total + 1 },
			}));
		} else {
			if (!commentId) {
				this.postCommentPresentation.updateContent(postId, (comments) => comments.unshift(result));
				this.postStore.update(postId, (post) => ({
					...post,
					comments: { ...post.comments, total: post.comments.total + 1 },
				}));
			}
		}
		return result;
	}

	async deleteComment(postId: string, commentId: string, parentId?: string): Promise<void> {
		await this.postApi.deleteComment(commentId, this.currentProfileService.organizationAlias.get()?.id);
		this.postCommentStore.remove(commentId);
		if (parentId) {
			this.commentRepliesPresentation.fetch(parentId); // ?
		} else {
			this.postCommentPresentation.fetch(postId);
			this.postStore.update(postId, (post) => ({
				...post,
				comments: {
					...post.comments,
					total: Math.max(0, post.comments.total - 1),
				},
			}));
		}
	}

	async togglePostCommentLike(postComment: PostComment): Promise<void> {
		const initialLiked = postComment.likes.byMe;

		this.postCommentStore.update(postComment.id, (postComment: PostComment): PostComment => ({
			...postComment,
			likes: { byMe: !initialLiked, total: postComment.likes.total + (initialLiked ? -1 : 1) },
		}));

		await (initialLiked
			? this.postApi.unLikeComment(postComment.id, this.currentProfileService.organizationAlias.get()?.id)
			: this.postApi.likeComment(postComment.id, this.currentProfileService.organizationAlias.get()?.id));
	}

	getObservableFeed(id?: string, source: FeedSource = "user"): Observable<PaginatedData<Post> | null> {
		return id
			? source === "organization"
				? this.organizationsFeed.getObservable(id)
				: source === "group"
					? this.groupsFeed.getObservable(id)
					: this.usersFeed.getObservable(id)
			: this.homeFeed.paginatedItems;
	}

	fetchFeed(id?: string, source: FeedSource = "user"): Promise<void> {
		if (!id) {
			this.hasNewContent.set(false);
		}
		return id
			? source === "organization"
				? this.organizationsFeed.fetch(id)
				: source === "group"
					? this.groupsFeed.fetch(id)
					: this.usersFeed.fetch(id)
			: this.homeFeed.fetch();
	}
	fetchMoreFeed(id?: string, source: FeedSource = "user"): Promise<void> {
		return id
			? source === "organization"
				? this.organizationsFeed.fetchMore(id)
				: source === "group"
					? this.groupsFeed.fetchMore(id)
					: this.usersFeed.fetchMore(id)
			: this.homeFeed.fetchMore();
	}

	getReactionStore(reaction: PostReactionType): MappedStore<Profile, string> {
		if (reaction === "likes") {
			return this.likersStore;
		}
		if (reaction === "share") {
			return this.sharersStore;
		}
		return this.commentersStore;
	}

	getCommentsReaction(): MappedStore<Profile, string> {
		return this.commentersLikesStore;
	}

	async checkNewContent(): Promise<void> {
		const result = await this.postApi.list({ page: 0, size: 1 });
		const currenItems = this.homeFeed.paginatedItems.get();
		if (currenItems && currenItems.total_size < result.total_size) {
			this.hasNewContent.set(true);
		}
	}
}
